import React, { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyles from '@mui/material/GlobalStyles';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, X, Wifi, AlertCircle, Maximize2, Minimize2, Copy, Share2, ExternalLink, Check } from 'lucide-react';
import videoApiService, { VideoRoom } from '../services/videoApiService';

// ─── Color tokens (matches existing theme) ──────────────────────────────────
const C = {
  navy:   '#0A0A0A',
  teal:   '#2563EB',
  tealLt: '#EFF6FF',
  white:  '#FFFFFF',
  bg:     '#FAFAFA',
  border: 'rgba(0, 0, 0, 0.08)',
  text2:  '#525252',
  text3:  '#A3A3A3',
  error:  '#EF4444',
  errorLt:'#FEF2F2',
};

interface Props {
  open: boolean;
  onClose: () => void;
  deptId?: number | string;
  context?: string;       // "hospital-consult" | "dept-consult"
  hospitalName?: string;
}

type Phase = 'form' | 'loading' | 'call' | 'error';

export default function VideoConsultModal({ open, onClose, deptId, context, hospitalName }: Props) {
  const [phase, setPhase] = useState<Phase>('form');
  const [patientName, setPatientName] = useState('');
  const [room, setRoom] = useState<VideoRoom | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setPhase('form');
        setPatientName('');
        setRoom(null);
        setErrorMsg('');
        setIsFullScreen(false);
        setCopyStatus('idle');
      }, 300);
    }
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const startCall = useCallback(async () => {
    setPhase('loading');
    try {
      const newRoom = await videoApiService.createRoom({
        patientName: patientName.trim() || undefined,
        deptId,
        context: context || 'video-consult',
      });
      setRoom(newRoom);
      setPhase('call');
    } catch (e: any) {
      setErrorMsg(e?.message || 'Could not start video call. Please try again.');
      setPhase('error');
    }
  }, [patientName, deptId, context]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  const participantLink = room?.participantRoomUrl || room?.participantUrl || room?.viewerRoomUrl || room?.roomUrl || '';

  const copyParticipantLink = useCallback(async () => {
    if (!participantLink) return;
    try {
      await navigator.clipboard.writeText(participantLink);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('error');
    }
  }, [participantLink]);

  const shareParticipantLink = useCallback(async () => {
    if (!participantLink) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join video consultation',
          text: 'Use this link to join the video consultation.',
          url: participantLink,
        });
        return;
      } catch {
        // Fall back to clipboard when native share is cancelled/unavailable.
      }
    }

    await copyParticipantLink();
  }, [participantLink, copyParticipantLink]);

  useEffect(() => {
    if (copyStatus === 'idle') return;
    const timer = window.setTimeout(() => setCopyStatus('idle'), 2200);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  if (!open) return null;

  // ─── Whereby iframe URL with embed params ─────────────────────────────────
  const iframeSrc = room
    ? `${room.roomUrl}?embed&skipMediaPermissionPrompt&leaveButton=on&chat=on&people=on`
    : '';

  return (
    <AnimatePresence>
      {open && (
        <>
          <GlobalStyles styles={`
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');
          `} />

          {/* ── Backdrop ── */}
          <Box
            component={motion.div as any}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            sx={{
              position: 'fixed', inset: 0, zIndex: 9000,
              background: 'rgba(0, 0, 0, 0.72)',
              backdropFilter: 'blur(8px)',
            }}
          />

          {/* ── Modal Panel ── */}
          <Box
            component={motion.div as any}
            initial={{ opacity: 0, scale: 0.95, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 24 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            onKeyDown={handleKeyDown}
            sx={{
              position: 'fixed', zIndex: 9001,
              ...(isFullScreen
                ? { inset: 0, borderRadius: 0 }
                : {
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '96vw', sm: '600px', md: '820px' },
                    maxHeight: '90vh',
                    borderRadius: '24px',
                  }),
              bgcolor: C.white,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)',
            }}
          >
            {/* ── Header ── */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 3, py: 2,
              background: `linear-gradient(135deg, ${C.navy} 0%, #1e293b 100%)`,
              flexShrink: 0,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '10px',
                  background: 'rgba(37,99,235,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Video size={18} color="#60A5FA" />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 15, fontWeight: 700, color: C.white, lineHeight: 1.2 }}>
                    Video Consultation
                  </Typography>
                  {hospitalName && (
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                      {hospitalName}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Full-screen toggle */}
                <Box
                  onClick={() => setIsFullScreen(f => !f)}
                  sx={{
                    width: 32, height: 32, borderRadius: '8px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.6)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: C.white },
                    transition: 'all 0.2s',
                  }}
                >
                  {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </Box>
                {/* Close */}
                <Box
                  onClick={onClose}
                  sx={{
                    width: 32, height: 32, borderRadius: '8px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.6)',
                    '&:hover': { bgcolor: 'rgba(239,68,68,0.2)', color: '#FCA5A5' },
                    transition: 'all 0.2s',
                  }}
                >
                  <X size={16} />
                </Box>
              </Box>
            </Box>

            {/* ── Body ── */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

              {/* Phase: Form */}
              {phase === 'form' && (
                <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Illustration area */}
                  <Box sx={{
                    background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)',
                    borderRadius: '20px', p: 4, textAlign: 'center',
                    border: `1px solid rgba(37,99,235,0.1)`,
                  }}>
                    <Box sx={{
                      width: 80, height: 80, mx: 'auto', mb: 2,
                      background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 100%)',
                      borderRadius: '24px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: '0 12px 24px -6px rgba(37,99,235,0.4)',
                    }}>
                      <Video size={36} color="white" />
                    </Box>
                    <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 22, color: C.navy, mb: 1 }}>
                      Start Video Consultation
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: C.text2, lineHeight: 1.6, maxWidth: 360, mx: 'auto' }}>
                      Connect with our medical team instantly. Your video call will be secure and private.
                    </Typography>
                  </Box>

                  {/* Name input */}
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy, mb: 1 }}>
                      Your Name <Box component="span" sx={{ color: C.text3, fontWeight: 400 }}>(optional)</Box>
                    </Typography>
                    <Box
                      component="input"
                      value={patientName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPatientName(e.target.value)}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && startCall()}
                      placeholder="Enter your name..."
                      sx={{
                        width: '100%', px: '16px', py: '14px',
                        borderRadius: '12px', border: `1.5px solid ${C.border}`,
                        fontSize: 15, color: C.navy, fontFamily: 'Inter, sans-serif',
                        outline: 'none', boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                        '&:focus': { borderColor: C.teal, boxShadow: `0 0 0 3px rgba(37,99,235,0.1)` },
                        bgcolor: C.white,
                      }}
                    />
                  </Box>

                  {/* Privacy note */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#F0FDF4', p: 2, borderRadius: '12px', border: '1px solid #BBF7D0' }}>
                    <Wifi size={16} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
                    <Typography sx={{ fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                      Your camera & microphone permissions will be requested by the browser. The call is end-to-end encrypted.
                    </Typography>
                  </Box>

                  {/* CTA */}
                  <Box
                    component="button"
                    onClick={startCall}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                      bgcolor: C.teal, color: C.white,
                      border: 'none', borderRadius: '14px',
                      px: 4, py: '16px', fontSize: 16, fontWeight: 700,
                      cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
                      transition: 'all 0.25s',
                      boxShadow: '0 8px 20px -6px rgba(37,99,235,0.5)',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 28px -6px rgba(37,99,235,0.6)', bgcolor: '#1D4ED8' },
                      '&:active': { transform: 'translateY(0)' },
                    }}
                  >
                    <Video size={20} />
                    Join Video Call
                  </Box>
                </Box>
              )}

              {/* Phase: Loading */}
              {phase === 'loading' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 6 }}>
                  <Box sx={{
                    width: 72, height: 72, borderRadius: '20px',
                    background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 1,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { boxShadow: '0 0 0 0 rgba(37,99,235,0.4)' },
                      '50%': { boxShadow: '0 0 0 16px rgba(37,99,235,0)' },
                    }
                  }}>
                    <Video size={32} color="white" />
                  </Box>
                  <CircularProgress size={28} sx={{ color: C.teal }} />
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 18, color: C.navy }}>
                    Setting up your room...
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: C.text2 }}>
                    This takes just a second
                  </Typography>
                </Box>
              )}

              {/* Phase: Error */}
              {phase === 'error' && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 6 }}>
                  <Box sx={{
                    width: 72, height: 72, borderRadius: '20px',
                    bgcolor: C.errorLt, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1,
                  }}>
                    <AlertCircle size={36} color={C.error} />
                  </Box>
                  <Typography sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 18, color: C.navy }}>
                    Connection Failed
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: C.text2, textAlign: 'center', maxWidth: 320 }}>
                    {errorMsg}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Box
                      component="button"
                      onClick={() => setPhase('form')}
                      sx={{
                        px: 3, py: '12px', borderRadius: '12px', fontSize: 14, fontWeight: 600,
                        border: `1.5px solid ${C.border}`, bgcolor: C.white, color: C.navy,
                        cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                        '&:hover': { bgcolor: C.bg },
                      }}
                    >
                      Go Back
                    </Box>
                    <Box
                      component="button"
                      onClick={startCall}
                      sx={{
                        px: 3, py: '12px', borderRadius: '12px', fontSize: 14, fontWeight: 700,
                        bgcolor: C.teal, color: C.white, border: 'none',
                        cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                        '&:hover': { bgcolor: '#1D4ED8' },
                      }}
                    >
                      Try Again
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Phase: Active Call — Whereby iframe */}
              {phase === 'call' && room && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                  {/* Call info bar */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5,
                    bgcolor: '#F8FAFF', borderBottom: `1px solid ${C.border}`, flexShrink: 0,
                    flexWrap: 'wrap',
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22C55E', flexShrink: 0,
                      animation: 'blink 1.5s ease-in-out infinite',
                      '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }
                    }} />
                    <Typography sx={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>Call Active</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Typography sx={{ fontSize: 11, color: C.text2 }}>
                        Participant link ready
                      </Typography>
                      <Box
                        component="button"
                        onClick={copyParticipantLink}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.75,
                          px: 1.5, py: '7px', borderRadius: '999px',
                          border: `1px solid ${C.border}`, bgcolor: C.white, color: C.navy,
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        {copyStatus === 'copied' ? <Check size={14} /> : <Copy size={14} />}
                        {copyStatus === 'copied' ? 'Copied' : 'Copy link'}
                      </Box>
                      <Box
                        component="button"
                        onClick={shareParticipantLink}
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.75,
                          px: 1.5, py: '7px', borderRadius: '999px',
                          border: `1px solid ${C.border}`, bgcolor: C.white, color: C.navy,
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        <Share2 size={14} />
                        Share
                      </Box>
                      <Box
                        component="a"
                        href={participantLink}
                        target="_blank"
                        rel="noreferrer"
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.75,
                          px: 1.5, py: '7px', borderRadius: '999px',
                          border: `1px solid ${C.border}`, bgcolor: C.white, color: C.navy,
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        <ExternalLink size={14} />
                        Open link
                      </Box>
                    </Box>
                    <Wifi size={14} color={C.text3} />
                    <Typography sx={{ fontSize: 11, color: C.text3 }}>
                      Secured by Unofy Trust Link
                    </Typography>
                  </Box>

                  {/* Whereby iframe */}
                  <Box
                    component="iframe"
                    src={iframeSrc}
                    allow="camera; microphone; fullscreen; speaker-selection; display-capture; autoplay"
                    sx={{
                      flex: 1, border: 'none',
                      width: '100%',
                      minHeight: isFullScreen ? 'calc(100vh - 112px)' : { xs: '320px', sm: '380px', md: '420px' },
                      display: 'block',
                    }}
                    title="Video Consultation"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </AnimatePresence>
  );
}
